export type BoostPolicyKnowledgeDocument = {
  id: string;
  title: string;
  description: string;
  tags: string[];
  fileType: string;
  categoryMapping: { categoryId: string; subcategoryId?: string | null } | null;
};

export type BoostPolicyTag = {
  id: string;
  name: string;
  category: string;
};

export type BoostPolicyBrandingData = {
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  font_color?: string;
  font_family?: string;
  logo_url?: string;
  brand_title?: string;
  tagline?: string;
  logo_position?: string;
  theme_mode?: string;
  [key: string]: unknown;
};

export type OrganizationProfileData = {
  industry?: string;
  org_size?: string;
  jurisdiction?: string;
  standards?: string[];
  maturityLevel?: string;
};

// Answers keyed by category UUID, then question UUID.
// Values: string (yes-no, single-select, text, textarea) or string[] (multi-select).
export type StandardAnswers = Record<string, Record<string, string | string[]>>;

export type OrganizationContextData = {
  organizationContext?: string;
  profile?: OrganizationProfileData;
  standard_answers?: StandardAnswers;
};

export type SelectedTemplateData = {
  id: string;
  name: string;
  content: string | null;
  bucket_path: string | null;
};

export type BoostPolicyFormData = {
  policyName: string;
  selectedKnowledgeDocument: BoostPolicyKnowledgeDocument | null;
  organizationContext: string;
  industryTags: BoostPolicyTag[];
  regionTags: BoostPolicyTag[];
  workforceSize: string;
  selectedSkillIds: string[];
  selectedTemplateId: string | null;
  selectedTemplateName: string | null;
  selectedTemplateData: SelectedTemplateData | null;
  relevantContext: string[];
  additionalContext: string;
};

// Re-export AiSkill from the altzero CRM foundation layer so revisr components
// can import it from a single location without coupling to the foundation path.
export type { AiSkill } from "@foundation/services/aiSkillsService";

export type BoostPolicyStepId =
  | "policy-library"
  | "policy-setup"
  | "context-qa"
  | "review";

export type BoostPolicyStep = {
  id: BoostPolicyStepId;
  title: string;
  description: string;
};

// Step name used in URL query params (?step=library)
export type BoostPolicyStepName = "library" | "setup" | "qa" | "review";

export const STEP_NAME_TO_INDEX: Record<BoostPolicyStepName, number> = {
  library: 0,
  setup: 1,
  qa: 2,
  review: 3,
};

export const STEP_INDEX_TO_NAME: Record<number, BoostPolicyStepName> = {
  0: "library",
  1: "setup",
  2: "qa",
  3: "review",
};

export type QAMessage = {
  role: 'ai' | 'user';
  text: string;
  timestamp: string;
  isStreaming?: boolean;
};

export type QAStreamEvent =
  | { type: 'content'; text: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

// DB record shape for revisr.revisr_final_policies (boost_policy workflow)
export type BoostPolicyRecord = {
  id: string;
  org_id: string;
  user_id: string;
  policy_name: string;
  status: "draft" | "completed";
  current_step: number;
  workflow_type: string;
  policy_store_id: string | null;
  generation_prompt: string | null;
  branding_data: BoostPolicyBrandingData | null;
  audit_results: {
    selectedKnowledgeDocument?: BoostPolicyKnowledgeDocument;
    industryTags?: BoostPolicyTag[];
    regionTags?: BoostPolicyTag[];
    workforceSize?: string;
    selectedSkillIds?: string[];
    selectedTemplateId?: string | null;
  } | null;
  template_store_id: string | null;
  policy_markdown_url: string | null;
  editor_content_url: string | null;
  markdown_url: string | null;
  // JSONB column — holds { standard_document?, classification? }. classification
  // is the Q&A Tier 0 answer (Public / Internal Use / Private / Confidential),
  // written at generation and refreshed from the editable footer on editor save.
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};
